import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Vision from "@/components/Vision";
import OurStory from "@/components/OurStory";
import ThreePillars from "@/components/ThreePillars";
import Testimonials from "@/components/Testimonials";
import CaseStudies from "@/components/CaseStudies";
import Founder from "@/components/Founder";
import Consulting from "@/components/Consulting";
import Blog from "@/components/Blog";
import FAQ from "@/components/FAQ";
import ContactForm from "@/components/ContactForm";
import Footer from "@/components/Footer";
import WhatsAppWidget from "@/components/WhatsAppWidget";
import Products from "@/components/Products";
import DemoLogin from "@/pages/DemoLogin";
import DemoDashboard from "@/pages/DemoDashboard";
import FacialRecognitionDemo from "@/pages/FacialRecognitionDemo";
import NewMinds from "@/pages/NewMinds";
import AdminBlog from "@/pages/AdminBlog";
import HLLanding from "@/pages/hl/HLLanding";
import HLAuth from "@/pages/hl/HLAuth";
import HLAspirantDashboard from "@/pages/hl/HLAspirantDashboard";
import HLCorporateDashboard from "@/pages/hl/HLCorporateDashboard";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { HLAuthProvider, useHLAuth } from "@/context/HLAuthContext";
import { Loader2 } from "lucide-react";

const HomePage = () => (
  <div className="min-h-screen bg-slate-950">
    <Header />
    <main>
      <Hero />
      <Vision />
      <OurStory />
      <ThreePillars />
      <Testimonials />
      <CaseStudies />
      <Founder />
      <Blog />
      <FAQ />
      <Products />
      <Consulting />
      <ContactForm />
    </main>
    <Footer />
    <WhatsAppWidget />
  </div>
);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;
  if (!user) return <Navigate to="/demo" replace />;
  return children;
}

function DemoLoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;
  if (user) return <Navigate to="/demo/dashboard" replace />;
  return <DemoLogin />;
}

function HLProtected({ children, requiredRole }) {
  const { user, loading } = useHLAuth();
  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>;
  if (!user) return <Navigate to={`/humanloop/${requiredRole}`} replace />;
  if (user.role !== requiredRole) return <Navigate to={`/humanloop/${user.role}/dashboard`} replace />;
  return children;
}

function HLAuthRoute({ role }) {
  const { user, loading } = useHLAuth();
  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>;
  if (user) return <Navigate to={`/humanloop/${user.role}/dashboard`} replace />;
  return <HLAuth role={role} />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <HLAuthProvider>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/new-minds" element={<NewMinds />} />
            <Route path="/admin/blog" element={<AdminBlog />} />
            <Route path="/demo" element={<DemoLoginRoute />} />
            <Route path="/demo/dashboard" element={<ProtectedRoute><DemoDashboard /></ProtectedRoute>} />
            <Route path="/demo/facial-recognition" element={<ProtectedRoute><FacialRecognitionDemo /></ProtectedRoute>} />
            {/* Human In The Loop */}
            <Route path="/humanloop" element={<HLLanding />} />
            <Route path="/humanloop/aspirant" element={<HLAuthRoute role="aspirant" />} />
            <Route path="/humanloop/corporate" element={<HLAuthRoute role="corporate" />} />
            <Route path="/humanloop/aspirant/dashboard" element={<HLProtected requiredRole="aspirant"><HLAspirantDashboard /></HLProtected>} />
            <Route path="/humanloop/corporate/dashboard" element={<HLProtected requiredRole="corporate"><HLCorporateDashboard /></HLProtected>} />
          </Routes>
        </HLAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
